import Pagination from "../../../../components/Pagination";
import Pill from "./components/Pill";
import {
  TR,
  TH,
  TD,
  TableHead,
  TableBody,
  Table,
  TableContainer,
  EmptyState,
  TotalMembers,
  Empty,
  MenuButton,
  IconLink,
  EmailWrapper,
  EmailText,
  ContactActions,
  NameCell,
  PlanCell,
  TotalCell,
  ActionsWrapper,
  DateText,
  CountText,
  VerifiedText,
  EmailGlobalStyles
} from "./styles";

import services from "../../../../services";
import EditMemberModal from './components/EditMemberModal';
import TableActionsMenu from './components/TableActionsMenu';
import { showModal } from "../../../../store/actions/modal";
import { useDispatch } from "react-redux";
import Button from "../../../../components/Button";
import moment from "moment";
import { dateSpliter } from "../../../../helpers/dates-hanlder";
import { useMemo, useState } from "react";
import CopyToClipboad from "../../../../components/CopyToClipboad";
import constants from "../../../../constants";
import Image from "../../../../components/Image";
import { formatPrice } from "../../../../helpers/price-formatter";
import ExpireMemberModal from "./components/CancelMemberModal";
import CancelInvitationModal from "./components/CancelInvitationModal";

import Icon from '../../../../components/Icon'
import TelegramIcon from "../../../../assets/icons/telegram.svg";
import WhatsappIcon from "../../../../assets/icons/whatsapp.svg";
import EmailIcon from "../../../../assets/icons/mail.svg";
import { useWindowSize } from "../../../../hooks/useWindowSize";

const Label = ({ countryCode, showCountryCode }) => {
  const country = constants?.countries?.find((c) => c.code === countryCode);
  return country ? (
    <>
      <Image src={country?.flag} width="20px" height="15px" alt="" />
      {showCountryCode && country?.code}
    </>
  ) : null;
};

const emptyState = (numrows = 8) => {
  return Array.from({ length: numrows }, (_, i) => (
    <TR key={`empty-row-${i}`}>
      <TD className="sticky-left"><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD><Empty /></TD>
      <TD className="sticky-right"><Empty /></TD>
    </TR>
  ));
};

interface Filter {
  [field: string]: (string | number)[];
}

interface ComponentProps {
  groupId?: number;
  states: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  data: {
    country: string;
    email: string;
    last_name: string;
    name: string;
    purchased_groups_count: string;
    external_name: string | null;
    status;
    chat_status: string;
    transactions_count;
    billing_cycles;
    migration_flag: string | null;
    remaining_billing_cycles;
    transactions_sum;
    current_amount;
    current_rejected_reason;
    started_at;
    expire_at;
    type;
    period;
    period_unit;
    currency;
    subscription_id: number | null;
    migration_old_subscription_id?: number;
    phone_number?: string;
  }[];
  filters: Filter[];
  pagination: {
    currentPage: string;
    from: number;
    lastPage: number;
    perPage: string;
    to: number;
    total: number;
  };
  goToPage: (page: number) => void;
}

const convertPeriodToLabel = (period, period_unit) => {
  const periodLabels = {
    month: { 1: 'mensual', 3: 'trimestral', 6: 'semestral' },
    year: { 1: 'anual' }
  };
  return periodLabels[period_unit]?.[period] || 'N/A';
};

const Component = ({
  groupId,
  states,
  data,
  filters,
  pagination,
  goToPage,
}: ComponentProps) => {
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);

  const getMigrationStatus = (status, oldSubscriptionId) => {
    const migrationStatus = {
      "to_migrate": "Aún no ⏳",
      "migrated": "Si ✅",
      "no_migration": "No corresp. ➖"
    }

    if (status === "no_migration" && !!oldSubscriptionId) {
      return migrationStatus.migrated
    }

    return migrationStatus[status]
  }

  // Memoized conditions for the <TH> elements
  const tableHeaders = useMemo(() => ({
    showCountry: data?.some(item => item?.country),
    showStatus: data?.some(item => item?.status),
    showStartedAt: data?.some(item => item?.started_at),
    showPurchasedGroupsCount: data?.some(item => item?.purchased_groups_count),
    showPaymentMethodVerified: data?.some(item => item?.migration_flag && item?.migration_flag !== "no_migration"),
    showExternalName: data?.some(item => item?.external_name)
  }), [data]);

  const getTotalMembersText = (number) => `${number} ${number > 1 ? "miembros" : "miembro"}`;

  const paginate = (page) => goToPage?.(page);

  const exportSubscribers = () => {
    if (groupId) {
      services?.group?.exportSubscribers(groupId, filters);
    }
  };

  const Actions = ({ record }) => {
    return (
      <ContactActions>
        {record?.phone_number ? (
          <IconLink
            href={`https://t.me/${record?.phone_number}`}
            target="_blank"
            title="Enviar mensaje por Telegram"
          >
            <Icon icon={TelegramIcon} size="18px" color="#0088cc" />
          </IconLink>
        ) : (
          <IconLink className="disabled" title="Número no disponible">
            <Icon icon={TelegramIcon} size="18px" color="#DADADA" />
          </IconLink>
        )}

        {record?.phone_number ? (
          <IconLink
            href={`https://wa.me/${record?.phone_number}`}
            target="_blank"
            title="Enviar mensaje por WhatsApp"
          >
            <Icon icon={WhatsappIcon} size="18px" color="#25D366" />
          </IconLink>
        ) : (
          <IconLink className="disabled" title="Número no disponible">
            <Icon icon={WhatsappIcon} size="18px" color="#DADADA" />
          </IconLink>
        )}

        <CopyToClipboad
          copy={record?.email}
          successStyle={{
            position: "fixed",
            top: "20px",
            right: "20px",
            whiteSpace: "nowrap",
            fontSize: "12px",
            backgroundColor: "#333",
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            zIndex: 99,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}
        >
          <IconLink
            style={{ cursor: 'pointer' }}
            title="Copiar email"
            onClick={(e) => e.preventDefault()}
          >
            <Icon icon={EmailIcon} size="18px" color="#6b5ce7" />
          </IconLink>
        </CopyToClipboad>
      </ContactActions>
    );
  };

  const EmailCell = ({ email }) => {
    if (!email) return null;

    return (
      <div style={{
        display: 'block',
        width: '100%',
        position: 'relative'
      }}>
        <CopyToClipboad
          copy={email}
          showChildren
          className="email-copy"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block'
          }}
          successStyle={{
            position: "fixed",
            top: "20px",
            right: "20px",
            whiteSpace: "nowrap",
            fontSize: "12px",
            backgroundColor: "#333",
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            zIndex: 99,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
          }}
        >
          <EmailWrapper style={{
            display: 'block',
            width: '100%',
            textAlign: 'left'
          }}>
            <EmailText title={`Click para copiar: ${email}`} style={{
              display: 'block',
              width: '100%',
              textAlign: 'left'
            }}>
              {email}
            </EmailText>
          </EmailWrapper>
        </CopyToClipboad>


      </div>
    );
  };

  if (!states?.loading && data?.length <= 0) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TR>
              <TH className="sticky-left" style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}>Nombre</TH>
              <TH className="center" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>País</TH>
              <TH style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }}>Estado</TH>
              <TH style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}>Fecha de<br />suscripción</TH>
              <TH style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>Total recibido</TH>
              <TH style={{ width: '250px', minWidth: '250px', maxWidth: '250px' }}>Correo electronico</TH>
              <TH style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}>Plan</TH>
              <TH className="center" style={{ width: '90px', minWidth: '90px', maxWidth: '90px' }}>Cobros<br />recibidos</TH>
              <TH className="center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>Contacto</TH>
              <TH className="sticky-right center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}>Acciones</TH>
            </TR>
          </TableHead>
        </Table>
        <EmptyState key="empty-state-member-list">
          {Object.values(filters)?.some(v => v.toString().trim().length > 0) ? `No hay resultados con los filtros aplicados` : `Aún no tienes suscriptores`}
        </EmptyState>
      </TableContainer>

    );
  }

  return (
    <>
      <EmailGlobalStyles />
      {pagination?.total > 0 && (
        <div style={{ textAlign: "right", marginBottom: "16px" }}>
          <TotalMembers>{getTotalMembersText(pagination?.total)}</TotalMembers>
          <Button
            type="button"
            options={{ type: "link", skin: "purple", size: "md" }}
            onClick={exportSubscribers}
            style={{ marginLeft: "12px" }}
          >
            Exportar listado
          </Button>
        </div>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TR>
              <TH className={!isMobile ? "sticky-left" : ""} style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}>Nombre</TH>
              {tableHeaders.showCountry && <TH className="center" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>País</TH>}
              {tableHeaders.showStatus && <TH style={{ width: '160px', minWidth: '160px', maxWidth: '160px' }}>Estado</TH>}
              {tableHeaders.showStartedAt && <TH style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}>Fecha de<br />suscripción</TH>}
              <TH style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>Total recibido</TH>
              <TH style={{ width: '250px', minWidth: '250px', maxWidth: '250px' }}>Info. de<br />contacto</TH>
              {tableHeaders.showPurchasedGroupsCount && <TH className="center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>Grupos<br />comprados</TH>}
              {tableHeaders.showExternalName && <TH style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}>Plan</TH>}
              {tableHeaders.showPaymentMethodVerified && <TH className="center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}>Verificó<br />Met. Pago</TH>}
              <TH className="center" style={{ width: '90px', minWidth: '90px', maxWidth: '90px' }}>Cobros<br />recibidos</TH>
              <TH className="center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>Contacto</TH>
              <TH className="sticky-right center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}>Acciones</TH>
            </TR>
          </TableHead>

          <TableBody>
            {states?.loading ? (
              emptyState()
            ) : (
              data?.map((item, index) => {
                const memberAt = dateSpliter(
                  moment.unix(
                    item?.started_at?.length === 13 ? Number(item?.started_at) / 1000 : item?.started_at
                  ).format("YYYY-MM-DDTHH:mm:ss.000Z")
                );

                return (
                  <TR key={`member-${item?.name}-${item?.last_name}-${index}`}>
                    <TD className={!isMobile ? "sticky-left" : ""}>
                      <NameCell>
                        <div className="name">{item?.name || "-"}</div>
                        {item?.last_name && (
                          <div className="last-name">{item?.last_name}</div>
                        )}
                      </NameCell>
                    </TD>

                    {tableHeaders.showCountry && (
                      <TD className="center">
                        {!!item?.country && <Label countryCode={item?.country} showCountryCode={false} />}
                      </TD>
                    )}

                    {tableHeaders.showStatus && (
                      <TD>
                        {!!item?.status && (
                          <Pill
                            subscriptionState={item?.status}
                            toolTipPositin={index >= data.length - 2 ? 'left-bottom' : 'left-top'}
                          />
                        )}
                      </TD>
                    )}

                    {tableHeaders.showStartedAt && (
                      <TD>
                        {!!item?.started_at && memberAt?.year !== "Invalid date" && (
                          <DateText>
                            {`${memberAt?.day?.number}-${memberAt?.month?.name?.short}-${memberAt?.year}`}
                          </DateText>
                        )}
                      </TD>
                    )}

                    <TD>
                      {(item?.transactions_sum !== undefined) && (
                        <TotalCell>
                          <div className="amount">
                            ${formatPrice(item?.transactions_sum)}
                          </div>
                          <div className="currency">
                            {item?.currency}
                          </div>
                        </TotalCell>
                      )}
                    </TD>

                    <TD className="email-cell">
                      {!!item?.email && <EmailCell email={item?.email} />}
                    </TD>

                    {tableHeaders.showPurchasedGroupsCount && (
                      <TD className="center">
                        {!!item?.purchased_groups_count && (
                          <CountText>{item?.purchased_groups_count}</CountText>
                        )}
                      </TD>
                    )}

                    {tableHeaders.showExternalName && (
                      <TD>
                        {!!item?.external_name && (
                          <PlanCell>
                            <div className="plan-name">
                              {item?.external_name}
                            </div>
                            <div className="plan-details">
                              {item?.type === "guest"
                                ? "Externo"
                                : `$${item?.current_amount || 0} /${convertPeriodToLabel(item?.period, item?.period_unit)}`
                              }
                            </div>
                          </PlanCell>
                        )}
                      </TD>
                    )}

                    {tableHeaders.showPaymentMethodVerified && (
                      <TD className="center">
                        <VerifiedText>
                          {getMigrationStatus(item?.migration_flag, item?.migration_old_subscription_id)}
                        </VerifiedText>
                      </TD>
                    )}

                    <TD className="center">
                      <CountText>
                        {item?.billing_cycles > 0 && item?.remaining_billing_cycles
                          ? `${item?.billing_cycles - item?.remaining_billing_cycles} de ${item?.billing_cycles}`
                          : `${item?.transactions_count || 0}`
                        }
                      </CountText>
                    </TD>

                    <TD>
                      <Actions record={item} />
                    </TD>

                    <TD className="sticky-right center">
                      {(item?.type === "guest" || item?.type === "recurring") && (
                        <ActionsWrapper>
                          <TableActionsMenu
                            position={index >= data.length - 3 ? 'left-bottom' : 'left-top'}
                          >
                            {(item?.status === "pending_invite") && (
                              <MenuButton
                                onClick={() => {
                                  setSelectedSubscriptionId(item?.subscription_id);
                                  dispatch(showModal('cancel-invitation-modal'));
                                }}
                                disabled={!item?.subscription_id}
                              >
                                Cancelar invitación
                              </MenuButton>
                            )}
                            {(item?.status !== "expired" && item?.type === "guest") && (
                              <MenuButton
                                onClick={() => {
                                  setSelectedSubscriptionId(item?.subscription_id);
                                  dispatch(showModal('edit-member-modal'));
                                }}
                                disabled={item?.status === "expired" || item?.type !== "guest"}
                              >
                                Editar
                              </MenuButton>
                            )}

                            <MenuButton
                              onClick={() => {
                                setSelectedSubscriptionId(item?.subscription_id);
                                dispatch(showModal('expire-member-modal'));
                              }}
                              disabled={!(
                                (item?.status === "active" || item?.status === "renewal_retrying") &&
                                (item?.type === "guest" || item?.type === "recurring")
                              )}
                            >
                              Cancelar suscripción
                            </MenuButton>
                          </TableActionsMenu>
                        </ActionsWrapper>
                      )}
                    </TD>
                  </TR>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditMemberModal setSelectedSubscriptionId={setSelectedSubscriptionId} subscriptionId={selectedSubscriptionId} />
      <ExpireMemberModal subscriptionId={selectedSubscriptionId} />
      <CancelInvitationModal setSelectedSubscriptionId={setSelectedSubscriptionId} subscriptionId={selectedSubscriptionId} />

      {pagination?.total > 0 && (
        <div style={{ marginTop: "20px" }}>
          <Pagination
            states={states}
            key="community-subscriptions-pagination"
            total={pagination?.total}
            from={pagination?.from}
            to={pagination?.to}
            currentPage={pagination?.currentPage}
            lastPage={pagination?.lastPage}
            onClick={paginate}
          />
        </div>
      )}
    </>
  );
};

export default Component;